/**
 * Workflows P5-5: the Data connector (`http_request` step) and the connector authentication of a webhook endpoint.
 * Mirrors the limits of the backend step model (`definition_p5.py`, `graph_p5.py`); the server is the judge, these let
 * the builder refuse a mistake before the save does.
 */

export const CONNECTOR_METHODS = ["GET", "POST", "PUT", "PATCH"] as const;
export type ConnectorMethod = (typeof CONNECTOR_METHODS)[number];
/** Methods that carry a JSON body. */
export const CONNECTOR_BODY_METHODS: ReadonlySet<ConnectorMethod> = new Set(["POST", "PUT", "PATCH"]);

/** Limits of one connector request. Timeouts are milliseconds; a customer-facing workflow waits for the answer, so it is capped lower. */
export const CONNECTOR_LIMITS = {
  path: 300,
  queryRows: 10,
  queryKey: 40,
  queryValue: 500,
  headerRows: 10,
  headerName: 40,
  headerValue: 500,
  body: 16 * 1024,
  timeoutMsMin: 1000,
  timeoutMsMax: 10_000,
  timeoutMsDefault: 5000,
  facingTimeoutMsMax: 5000,
  mapRows: 20,
  pointer: 200,
  variableName: 40,
  sampleRows: 20,
  sampleValue: 500,
  /** Calls one run may make (shared with Send webhook) and per workspace per minute. */
  callsPerRun: 20,
  callsPerMinute: 600,
} as const;

export const CONNECTOR_QUERY_KEY_PATTERN = /^[A-Za-z0-9_.-]{1,40}$/;
export const CONNECTOR_HEADER_NAME_PATTERN = /^[A-Za-z0-9-]{1,40}$/;
/** `map[].to`: a run variable. */
export const CONNECTOR_MAP_TO_PATTERN = /^var\.[a-z0-9_]{1,40}$/;
export const CONNECTOR_VARIABLE_PREFIX = "var.";
/** `step.<name>.ok` / `.status` are readable by the steps after a connector request. */
export const CONNECTOR_FACTS = ["ok", "status"] as const;

/** Headers a workflow definition may not set (definitions are versioned and readable): the credentials go on the endpoint. */
export const CONNECTOR_FORBIDDEN_HEADERS: ReadonlySet<string> = new Set([
  "authorization",
  "cookie",
  "proxy-authorization",
  "x-api-key",
  "host",
  "content-length",
  "transfer-encoding",
]);
export const CONNECTOR_FORBIDDEN_HEADER_PREFIX = "x-sagot-";
/** Headers the endpoint's connector authentication may not use (the transport and Sagot's own headers). */
export const CONNECTOR_AUTH_FORBIDDEN_HEADERS: ReadonlySet<string> = new Set(["proxy-authorization", "host", "content-length", "transfer-encoding"]);
export const CONNECTOR_AUTH_LIMITS = { header: 40, value: 500 } as const;

/** Why a request failed (`error_type` of the test route, `reason` in the run log). */
export const CONNECTOR_ERROR_TYPES = [
  "endpoint_unavailable",
  "blocked_destination",
  "bad_request",
  "timeout",
  "transport",
  "http_status",
  "too_large",
  "invalid_json",
  "rate_limited",
  "run_cap",
  "time_budget",
  "plan",
] as const;
export type ConnectorErrorType = (typeof CONNECTOR_ERROR_TYPES)[number];
export const isConnectorErrorType = (value: string): value is ConnectorErrorType => CONNECTOR_ERROR_TYPES.some((type) => type === value);

/** JSON types of a response field, as the test route reports them. */
export const CONNECTOR_POINTER_TYPES = ["string", "number", "boolean", "null", "array", "object"] as const;
export type ConnectorPointerType = (typeof CONNECTOR_POINTER_TYPES)[number];
/** Only these end up in a variable (arrays and objects are skipped). */
export const CONNECTOR_MAPPABLE_TYPES: ReadonlySet<string> = new Set(["string", "number", "boolean"]);

/** `event` of the delivery-log row a connector request writes: a record of the call, never sent again (no redeliver). */
export const CONNECTOR_DELIVERY_EVENT = "workflow.connector";

/** Endpoint mode and message format a connector request can call. */
export const CONNECTOR_ENDPOINT_MODE = "workflows";
export const CONNECTOR_ENDPOINT_FORMAT = "sagot";
